import { faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Transition } from '@headlessui/react';
import { memo, useEffect, useMemo, useState } from 'react';
import { ChildrenV2, CommentV2 } from '~/components/discussions';
import { CommentV2 as CommentV2Type } from '~/lib/discussions/types';

interface CommentThreadProps {
  comment: CommentV2Type;
  mutateComment: () => void;
  moderator?: boolean;
  showAllReplies: boolean;
  isReview?: boolean;
}

/**
 * Replies to a comment (thread) allowing children to hide/show.
 */
function CommentThread({
  comment,
  mutateComment,
  moderator = false,
  showAllReplies = true,
  isReview = false,
}: Readonly<CommentThreadProps>) {
  const [showReplies, setShowReplies] = useState(showAllReplies);

  // when the show all/hide all replies changes update this node
  useEffect(() => {
    setShowReplies(showAllReplies);
  }, [showAllReplies]);

  const replyCount = useMemo(() => {
    return (
      (comment.children as CommentV2Type[]).reduce(
        (sum, child: CommentV2Type) => sum + (child.children?.length ?? 0),
        comment?.children?.length ?? 0,
      ) ?? 0
    );
  }, [comment]);

  return (
    <div key={comment.id}>
      <div className="rounded-lg shadow-lg">
        <CommentV2
          href={comment.display_path}
          comment={comment}
          moderator={moderator}
          mutateComment={mutateComment}
          isReview={isReview}
        />

        {!!(comment?.children?.length || 0) && (
          <div className="border-t bg-gray-100 p-4 sm:px-12">
            <div className="flex items-end justify-end">
              <button
                className="group inline-flex items-center gap-x-2 text-xs text-gray-500"
                onClick={() => setShowReplies(!showReplies)}
              >
                <FontAwesomeIcon
                  icon={faCaretUp}
                  className={`${showReplies ? '' : 'rotate-180'}`}
                />
                {showReplies ? 'Hide' : 'Show'} replies
                <div className="h-5 w-5 rounded-full border border-gray-500 text-center align-middle text-xs transition delay-150 ease-in-out group-hover:border-yellow-500 group-hover:text-yellow-500">
                  {replyCount}
                </div>
              </button>
            </div>
            <Transition
              show={showReplies}
              enter="transition-[max-height] duration-300 ease-in-out overflow-hidden"
              enterFrom="max-h-[0px]"
              enterTo="max-h-none"
              leave="transition-[max-height] duration-300 ease-in-out overflow-hidden"
              leaveFrom="max-h-none"
              leaveTo="max-h-[0px]"
            >
              <ChildrenV2
                root={comment}
                comments={comment.children ?? []}
                moderator={moderator}
                mutateComment={mutateComment}
                isReview={isReview}
              />
            </Transition>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(CommentThread);
